import Button from "@syntensor/common/components/button";
import getCopy from "@syntensor/common/data/copy";

import styles from "./create.module.css";
import { navigateTo } from "@syntensor/common/browser_history";
import Table, {
  deleteCellRenderer,
  inputCellRenderer,
  TableRef,
} from "@syntensor/common/components/table";
import Input from "@syntensor/common/components/input";
import { useEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import Modal from "react-modal";
import {
  fetchCreateProject,
  fetchProject,
  fetchProjectAdminUsers,
  fetchUsers,
} from "@syntensor/common/data/fetch_data";
import { CreateProjectPayload, ICognitoUser } from "@syntensor/common/types";
import { validEmail } from "../../studies/create/compounds/tableConfig";
import Loader from "@syntensor/common/components/loader";

interface IProjectMatchParams {
  0: string;
  groupId?: string;
  projectId?: string;
  cloneId?: string;
}

export default function CreateNewProjectForm() {
  const textAreaEl = useRef<HTMLTextAreaElement | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const { params } = useRouteMatch<IProjectMatchParams>();
  const { groupId: groupIdParam, projectId, cloneId } = params;

  const [groupId, setGroupId] = useState<string | undefined>(groupIdParam);
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [backUrl, setBackUrl] = useState(`/groups/${groupId ?? ""}`);
  const [adminUsers, setAdminUsers] = useState<Record<string, any>[]>([]);
  const [cognitoUsersCache, setCognitoUsersCache] = useState<ICognitoUser[]>(
    []
  );

  const modalTableRef = useRef<TableRef>(null);
  const tableRef = useRef<TableRef>(null);

  const tableConfig = {
    cols: [
      {
        id: "name",
        name: "Name",
        width: "33%",
      },
      {
        id: "email",
        name: "Email",
        width: "66%",
        inputCellRenderer: inputCellRenderer,
        validateHandler: async function (
          datum: Record<string, any>
        ): Promise<boolean> {
          const input = datum[this.id!];
          if (!validEmail(input)) {
            datum.name = "";
            datum.sub = "";
            return true;
          }

          let cognitoUsers = cognitoUsersCache;
          if (cognitoUsersCache.length === 0) {
            cognitoUsers = await fetchUsers();
            setCognitoUsersCache(cognitoUsers);
          }
          const match = cognitoUsers.find(
            (user: ICognitoUser) =>
              user.email.toLowerCase() === input.toLowerCase()
          );

          let invalid = true;

          if (match) {
            datum["name"] = `${match.given_name} ${match.family_name}`;
            datum["sub"] = match.sub;
            invalid = false;
          } else {
            datum.name = "";
            datum.sub = "";
          }

          return invalid;
        },
      },
    ],
  };

  function saveChanges() {
    if (modalTableRef.current) {
      const users = modalTableRef.current.getInternalState();

      let valid = true;
      for (let i = 0; i < users.length; i++) {
        if (users[i].invalidemail) {
          valid = false;
          i = users.length;
        }
      }

      if (!valid) return;

      const emails = users.map((user) => user.email);
      const hasDuplicates = emails.length !== new Set(emails).size;
      if (hasDuplicates) return;

      setAdminUsers(users);

      if (tableRef.current) {
        tableRef.current.setInternalState(users);
      }
    }

    setShowModal(false);
  }

  useEffect(() => {
    async function asyncFetch() {
      try {
        setIsLoading(true);
        if (projectId) {
          const [project, adminUsers] = await Promise.all([
            fetchProject(projectId),
            fetchProjectAdminUsers(projectId),
          ]);

          setAdminUsers(
            adminUsers.map((u: Record<string, string | number>) => {
              return {
                ...u,
                name: u.given_name + " " + u.family_name,
              };
            })
          );

          setGroupId(project.groupId);
          setBackUrl(`/groups/${project.groupId}`);
          setName(project.name ?? "");
          setDescription(project.description ?? "");
        }

        // this ID not used when saving
        if (cloneId) {
          const project = await fetchProject(cloneId);

          setGroupId(project.groupId);
          setBackUrl(`/groups/${project.groupId}`);
          setName(`${project.name ?? ""} clone`);
          setDescription(project.description ?? "");
        }

        setIsLoading(false);
      } catch (err) {
        console.error(err);
      }
    }

    asyncFetch();
  }, []);

  const back = () => {
    navigateTo(backUrl);
  };

  if (isLoading) {
    return <Loader />;
  }

  async function onSubmit() {
    // if (adminUsers.length === 0) {
    //   return;
    // }

    const payload: CreateProjectPayload = {
      name: name,
      description: description,
      id: projectId,
      groupId: groupId,
      projectAdminUsers: adminUsers,
    };

    await fetchCreateProject(payload);

    navigateTo(backUrl);
  }

  return (
    <div id="root" className={styles.newUserForm}>
      <form onSubmit={onSubmit}>
        <div className={styles.titleWrapper}>
          <h2 className={styles.title}>
            {getCopy("projects_project_configure")} / {name}
          </h2>

          <div className={styles.buttonsWrapper}>
            <Button role="subtle" onClick={back}>
              {getCopy("studies_search_discard-changes")}
              {/* {inputs.type && <>{getCopy("studies_search_discard-changes")}</>}
              {!inputs.type && <>{getCopy("studies_search_cancel")}</>} */}
            </Button>
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.compoundFormSection}>
            <h3 className={styles.formTitle}>
              Provide a name for your project
            </h3>
            <Input
              placeholder={getCopy("projects_create_name-placeholder")}
              name="displayName"
              defaultValue={name}
              onChange={(e: any) => {
                setName(e.target.value);
              }}
              required
            />
          </div>
        </div>

        <div className={styles.content}>
          <div className={styles.compoundFormSection}>
            <h3 className={styles.formTitle}>
              Add a description of your project
            </h3>
            <textarea
              ref={textAreaEl}
              name="text"
              // placeholder={placeholder}
              className={styles.textArea}
              defaultValue={description}
              onChange={(e: any) => {
                setDescription(e.target.value);
              }}
            />
          </div>
        </div>

        {/* <div className={styles.content}>
          <div style={{ marginBottom: "1em" }}>
            <h3 className={styles.formTitle}>
              Specify at least 1 project admin. Project admins can create, edit,
              and delete studies within the project
            </h3>

            <Table ref={tableRef} config={tableConfig} data={adminUsers} />
          </div>

          <div>
            <Button
              onClick={() => {
                setShowModal(true);
              }}
              role="secondary"
            >
              {getCopy("projects_project_edit-admins")}
            </Button>
          </div>
        </div> */}

        <div className={styles.content}>
          <div>
            <Button
              onClick={onSubmit}
              role="primary"
              isDisabled={name.length === 0 || description.length === 0}
            >
              {getCopy("projects_project_save")}
            </Button>
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          contentLabel="Project Admin Users"
          style={{
            overlay: {
              backgroundColor: "var(--primary-light)",
              zIndex: 99999,
            },
            content: {
              backgroundColor: "var(--primary-light)",
              position: "absolute",
              top: "0%",
              left: "0%",
              width: "100%",
              height: "100%",
              padding: "0",
              border: "0",
            },
          }}
        >
          <div className={styles.newUserForm}>
            <div
              className={styles.titleWrapper}
              style={{ borderBottom: "1px solid var(--outline-gray)" }}
            >
              <h2 className={styles.title}>
                {getCopy("projects_project_configure")} / {name}
              </h2>

              <div className={styles.buttonsWrapper}>
                <Button
                  role="subtle"
                  onClick={() => {
                    setShowModal(false);
                  }}
                >
                  {getCopy("studies_search_back")}
                </Button>

                <Button role="primary" onClick={saveChanges}>
                  {getCopy("studies_edit_save-changes")}
                </Button>
              </div>
            </div>

            <div className={styles.compoundForm} style={{ marginTop: "1rem" }}>
              <div>
                <div style={{ paddingRight: "0.5rem" }}>
                  <h3 className={styles.formTitle}>Project Admin Users</h3>

                  <Table
                    ref={modalTableRef}
                    config={{
                      ...tableConfig,
                      allowEditing: true,
                      cols: [
                        ...tableConfig.cols,
                        {
                          id: "delete",
                          name: "Remove",
                          width: "5%",
                          cellRenderer: deleteCellRenderer,
                        },
                      ],
                    }}
                    data={adminUsers}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </form>
    </div>
  );
}
